import React, { useCallback, useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Trash2, Pencil, Check, X, Loader2, Clock, RefreshCw, LogIn, LogOut, Users, AlertTriangle } from "lucide-react";

const TZ = Intl.DateTimeFormat().resolvedOptions().timeZone;

const emptyMovs = () => [
  { label: "Entrada", dir: "in" },
  { label: "Salida", dir: "out" },
];

function JornadaEditor({ initial, onCancel, onSaved }) {
  const editing = !!initial?.id;
  const [name, setName] = useState(initial?.name || "");
  const [movs, setMovs] = useState(initial?.movements?.length ? initial.movements.map((m) => ({ ...m })) : emptyMovs());
  const [saving, setSaving] = useState(false);

  const setMov = (i, k, v) => setMovs((arr) => arr.map((m, idx) => (idx === i ? { ...m, [k]: v } : m)));
  const addMov = () => setMovs((arr) => [...arr, { label: "", dir: arr.length % 2 === 0 ? "in" : "out" }]);
  const removeMov = (i) => setMovs((arr) => (arr.length > 2 ? arr.filter((_, idx) => idx !== i) : arr));

  const save = async () => {
    const cleanName = name.trim();
    const cleanMovs = movs.map((m) => ({ label: m.label.trim(), dir: m.dir === "out" ? "out" : "in", time: (m.time || "").trim() || null })).filter((m) => m.label);
    if (!cleanName) { toast.error("Ponle un nombre a la jornada"); return; }
    if (cleanMovs.length < 2) { toast.error("Agrega al menos 2 movimientos"); return; }
    setSaving(true);
    try {
      if (editing) await api.put(`/direccion/jornadas/${initial.id}`, { name: cleanName, movements: cleanMovs });
      else await api.post("/direccion/jornadas", { name: cleanName, movements: cleanMovs });
      toast.success(editing ? "Jornada actualizada" : "Jornada creada");
      onSaved();
    } catch (e) {
      toast.error(e.response?.data?.detail || "No se pudo guardar");
    } finally { setSaving(false); }
  };

  return (
    <div className="border border-navy/20 rounded-2xl p-5 bg-white" data-testid="jornada-editor">
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-display font-bold text-navy">{editing ? "Editar jornada" : "Nueva jornada personalizada"}</h3>
        <button onClick={onCancel} data-testid="jornada-editor-close" className="text-nardo-400 hover:text-navy p-1"><X className="w-4 h-4" /></button>
      </div>
      <label className="text-[10px] uppercase tracking-[0.25em] text-nardo-500">Nombre</label>
      <input value={name} onChange={(e) => setName(e.target.value)} data-testid="jornada-name-input" autoFocus
        placeholder="Ej. Media jornada, Turno vespertino…"
        className="mt-1 mb-4 w-full border-b-2 border-nardo-200 bg-transparent py-2 focus:border-navy outline-none text-navy" />
      <label className="text-[10px] uppercase tracking-[0.25em] text-nardo-500">Movimientos (en orden)</label>
      <div className="mt-2 space-y-2">
        {movs.map((m, i) => (
          <div key={i} className="flex items-center gap-2" data-testid={`jornada-mov-${i}`}>
            <span className="w-6 h-6 shrink-0 rounded-full bg-navy/10 text-navy text-xs font-bold flex items-center justify-center">{i + 1}</span>
            <input value={m.label} onChange={(e) => setMov(i, "label", e.target.value)} data-testid={`jornada-mov-label-${i}`}
              placeholder="Ej. Salida a comida" className="flex-1 min-w-0 border-b-2 border-nardo-200 bg-transparent py-1.5 focus:border-navy outline-none text-sm text-navy" />
            <input type="time" value={m.time || ""} onChange={(e) => setMov(i, "time", e.target.value)} data-testid={`jornada-mov-time-${i}`} title="Hora (opcional)"
              className="shrink-0 w-[7.5rem] border border-nardo-200 rounded-lg py-1.5 px-2 text-xs text-navy bg-white" />
            <select value={m.dir} onChange={(e) => setMov(i, "dir", e.target.value)} data-testid={`jornada-mov-dir-${i}`}
              className="shrink-0 border border-nardo-200 rounded-lg py-1.5 px-2 text-xs text-navy bg-white">
              <option value="in">Entrada</option>
              <option value="out">Salida</option>
            </select>
            <button onClick={() => removeMov(i)} disabled={movs.length <= 2} data-testid={`jornada-mov-del-${i}`}
              className="shrink-0 text-nardo-400 hover:text-red-600 p-1 disabled:opacity-30"><Trash2 className="w-4 h-4" /></button>
          </div>
        ))}
      </div>
      <button onClick={addMov} data-testid="jornada-mov-add" className="mt-3 inline-flex items-center gap-1.5 text-xs font-semibold text-navy border border-navy/30 rounded-lg px-3 py-1.5 hover:bg-navy/5">
        <Plus className="w-3.5 h-3.5" /> Agregar movimiento
      </button>
      <div className="flex gap-2 mt-5">
        <button onClick={save} disabled={saving} data-testid="jornada-save-btn"
          className="inline-flex items-center gap-2 bg-navy text-white text-sm font-semibold rounded-lg px-4 py-2 hover:bg-navy-ink disabled:opacity-50">
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />} Guardar
        </button>
        <button onClick={onCancel} className="text-sm font-semibold text-nardo-600 rounded-lg px-4 py-2 hover:bg-nardo-50">Cancelar</button>
      </div>
    </div>
  );
}

function LiveBoard() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const timer = useRef(null);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/direccion/jornada-hoy", { params: { tz: TZ } });
      setRows(data.rows || []);
    } catch { /* silencio */ } finally { setLoading(false); }
  }, []);

  useEffect(() => {
    load();
    timer.current = setInterval(load, 15000);
    return () => clearInterval(timer.current);
  }, [load]);

  return (
    <div data-testid="jornada-board">
      <div className="flex items-center justify-between mb-3">
        <h3 className="font-display font-bold text-navy flex items-center gap-2"><Clock className="w-5 h-5 text-navy-light" /> Control de jornada de hoy</h3>
        <button onClick={load} data-testid="jornada-board-refresh" className="inline-flex items-center gap-1.5 text-xs font-semibold text-navy border border-navy/20 rounded-lg px-3 py-1.5 hover:bg-navy/5">
          <RefreshCw className="w-3.5 h-3.5" /> Actualizar
        </button>
      </div>
      {loading ? (
        <div className="py-10 flex justify-center text-navy"><Loader2 className="w-6 h-6 animate-spin" /></div>
      ) : rows.length === 0 ? (
        <div className="py-10 text-center text-sm text-nardo-500 border border-dashed border-nardo-200 rounded-2xl">
          <Users className="w-8 h-8 mx-auto mb-2 text-nardo-300" />
          Aún no hay personal con jornada asignada. Asígnala en <strong>Docentes → Editar persona</strong>.
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 gap-3">
          {rows.map((r) => (
            <div key={r.id} className={`rounded-2xl border p-4 ${r.overdue_count > 0 ? "border-red-300 bg-red-50/50" : r.complete ? "border-emerald-300 bg-emerald-50/40" : "border-nardo-200 bg-white"}`} data-testid={`jornada-row-${r.id}`}>
              <div className="flex items-center justify-between gap-2 mb-2">
                <div className="min-w-0">
                  <div className="font-semibold text-navy text-sm truncate">{r.name}</div>
                  <div className="text-[11px] text-nardo-500 truncate">{r.jornada_name}</div>
                </div>
                <div className="flex items-center gap-1.5 shrink-0">
                  {r.overdue_count > 0 && (
                    <span className="inline-flex items-center gap-1 text-[11px] font-bold px-2 py-0.5 rounded-full bg-red-100 text-red-700" data-testid={`jornada-overdue-${r.id}`}>
                      <AlertTriangle className="w-3 h-3" /> {r.overdue_count} sin marcar
                    </span>
                  )}
                  <span className={`text-[11px] font-bold px-2 py-0.5 rounded-full ${r.complete ? "bg-emerald-100 text-emerald-700" : "bg-navy/10 text-navy"}`} data-testid={`jornada-progress-${r.id}`}>
                    {r.done}/{r.total}
                  </span>
                </div>
              </div>
              <div className="flex flex-wrap gap-1.5">
                {r.movements.map((m, i) => {
                  const isIn = m.dir === "in";
                  const Icon = m.overdue ? AlertTriangle : (isIn ? LogIn : LogOut);
                  return (
                    <span key={i} data-testid={`jornada-chip-${r.id}-${i}`}
                      className={`inline-flex items-center gap-1 text-[11px] rounded-full px-2 py-1 border ${
                        m.done
                          ? (isIn ? "bg-emerald-100 border-emerald-200 text-emerald-800" : "bg-blue-100 border-blue-200 text-blue-800")
                          : m.overdue
                            ? "bg-red-100 border-red-300 text-red-700 font-semibold"
                            : "border-dashed border-nardo-300 text-nardo-400"}`}>
                      <Icon className="w-3 h-3" />
                      {m.label}{m.done && m.time ? ` · ${m.time}` : (!m.done && m.expected ? ` · esp. ${m.expected}` : "")}
                    </span>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default function JornadasAdmin({ showBoard = true }) {
  const [jornadas, setJornadas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // null | {} (new) | jornada (edit)

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/direccion/jornadas");
      setJornadas(data.jornadas || []);
    } catch { /* silencio */ } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const del = async (jid) => {
    if (!window.confirm("¿Eliminar esta jornada? Se quitará de quien la tuviera asignada.")) return;
    try { await api.delete(`/direccion/jornadas/${jid}`); toast.success("Jornada eliminada"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "No se pudo eliminar"); }
  };

  return (
    <div className="space-y-8" data-testid="jornadas-admin">
      <div>
        <div className="flex items-center justify-between mb-3">
          <div>
            <h3 className="font-display font-bold text-navy">Catálogo de jornadas</h3>
            <p className="text-sm text-nardo-500">Perfiles de movimientos por día para personal y docentes. Asígnalos por persona en <strong>Docentes → Editar</strong>.</p>
          </div>
          {!editing && (
            <button onClick={() => setEditing({})} data-testid="jornada-new-btn"
              className="shrink-0 inline-flex items-center gap-2 bg-navy text-white text-sm font-semibold rounded-lg px-4 py-2 hover:bg-navy-ink">
              <Plus className="w-4 h-4" /> Nueva jornada
            </button>
          )}
        </div>

        {editing ? (
          <JornadaEditor initial={editing.id ? editing : null} onCancel={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />
        ) : loading ? (
          <div className="py-8 flex justify-center text-navy"><Loader2 className="w-6 h-6 animate-spin" /></div>
        ) : (
          <div className="grid sm:grid-cols-2 gap-3">
            {jornadas.map((j) => (
              <div key={j.id} className="rounded-2xl border border-nardo-200 bg-white p-4" data-testid={`jornada-card-${j.id}`}>
                <div className="flex items-start justify-between gap-2 mb-2">
                  <div className="min-w-0">
                    <div className="font-semibold text-navy text-sm">{j.name}</div>
                    <div className="text-[11px] text-nardo-500">{j.movements.length} movimientos {j.builtin && <span className="ml-1 px-1.5 py-0.5 rounded-full bg-nardo-100 text-nardo-500 text-[10px] font-semibold">Predefinida</span>}</div>
                  </div>
                  {!j.builtin ? (
                    <div className="flex items-center gap-1 shrink-0">
                      <button onClick={() => setEditing(j)} data-testid={`jornada-edit-${j.id}`} className="text-nardo-400 hover:text-navy p-1" title="Editar"><Pencil className="w-4 h-4" /></button>
                      <button onClick={() => del(j.id)} data-testid={`jornada-del-${j.id}`} className="text-nardo-400 hover:text-red-600 p-1" title="Eliminar"><Trash2 className="w-4 h-4" /></button>
                    </div>
                  ) : (
                    <button onClick={() => setEditing({ name: `${j.name} (personalizada)`, movements: j.movements.map((m) => ({ ...m })) })} data-testid={`jornada-dup-${j.id}`}
                      className="shrink-0 inline-flex items-center gap-1 text-[11px] font-semibold text-navy border border-navy/30 rounded-lg px-2 py-1 hover:bg-navy/5" title="Crear una copia editable con horas">
                      <Pencil className="w-3.5 h-3.5" /> Personalizar
                    </button>
                  )}
                </div>
                <div className="flex flex-wrap gap-1.5">
                  {j.movements.map((m, i) => {
                    const isIn = m.dir === "in";
                    return (
                      <span key={i} className={`inline-flex items-center gap-1 text-[11px] rounded-full px-2 py-1 ${isIn ? "bg-emerald-50 text-emerald-700" : "bg-blue-50 text-blue-700"}`}>
                        <span className="font-bold">{i + 1}.</span> {m.label}{m.time ? ` · ${m.time}` : ""}
                      </span>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {showBoard && (
        <div className="border-t border-nardo-200 pt-6">
          <LiveBoard />
        </div>
      )}
    </div>
  );
}
